import React, { useState } from 'react';
import { Modal, Button, Upload, message, Alert, List, Typography, Steps, Progress, Spin, Collapse, Tag } from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  InfoCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import {
  downloadCSVTemplate,
  parseCSVFile,
  validateAllRows,
  buildTaskCreateData,
} from '../utils/bulkTaskImport.utils';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import { useOrgContext } from '../../../../core/components/orgs/useOrgContext';
import {
  taskTypes,
  subTypes,
  urgencyTypes,
} from '../configs/task.config';
import { useWindowWidth } from '../../../../core/hooks/useWindowWidth';

const { Text, Title, Paragraph } = Typography;
const { Step } = Steps;
const { Panel } = Collapse;

const BulkTaskImportModal = ({ visible, onClose, onSuccess }) => {
  const { currentUser, createTask } = useTaskContext();
  const { queryOrgs } = useOrgContext();
  const windowWidth = useWindowWidth();

  const [currentStep, setCurrentStep] = useState(0);
  const [csvFile, setCsvFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState({ success: [], failed: [] });
  const [orgMappings, setOrgMappings] = useState({});

  const handleDownloadTemplate = () => {
    downloadCSVTemplate();
    message.success('Template downloaded successfully');
  };

  const getSystemOrgName = (userInput, type, channelPartnerName = null) => {
    if (!userInput) return null;

    let key;
    if (type === 'channelPartner') {
      key = `cp_${userInput.trim()}`;
    } else if (type === 'advertiser' && channelPartnerName) {
      key = `adv_${channelPartnerName.trim()}::${userInput.trim()}`;
    } else if (type === 'assignee') {
      key = `assignee_${userInput.trim()}`;
    }

    const org = orgMappings[key];
    return org ? org.name : userInput; 
  };

  const validateOrganizations = async (rows) => {
    const errors = [];
    const warnings = [];
    const foundOrgs = {}; 

    const channelPartnerNames = new Set();
    const advertiserNames = new Set();
    const assigneeOrgNames = new Set();

    rows.forEach((row) => {
      const channelPartner = row.data['Channel Partner Name*']?.trim();
      const advertiser = row.data['Advertiser Name*']?.trim();
      const assigneeOrg = row.data['Assignee Org Name']?.trim();

      if (channelPartner) channelPartnerNames.add(channelPartner);
      if (advertiser) advertiserNames.add(advertiser);
      if (assigneeOrg) assigneeOrgNames.add(assigneeOrg);
    });

    for (const channelPartnerName of channelPartnerNames) {
      try {
        const data = await queryOrgs({
          first: 1,
          skip: 0,
          where: {
            name: channelPartnerName,
          },
        });

        if (!data?.orgs || data.orgs.length === 0) {
          const affectedRows = rows
            .filter(r => r.data['Channel Partner Name*']?.trim() === channelPartnerName)
            .map(r => r.lineNumber);

          errors.push(
            `❌ Rows ${affectedRows.join(', ')}: Channel Partner "${channelPartnerName}" not found in system. Please verify the organization name matches exactly (case-sensitive).`
          );
        } else {
          foundOrgs[`cp_${channelPartnerName}`] = data.orgs[0];
        }
      } catch (error) {
        errors.push(
          `❌ Failed to verify Channel Partner "${channelPartnerName}": ${error.message}`
        );
      }
    }

    const advertiserValidationMap = new Map();

    for (const row of rows) {
      const channelPartnerName = row.data['Channel Partner Name*']?.trim();
      const advertiserName = row.data['Advertiser Name*']?.trim();

      if (!channelPartnerName || !advertiserName) continue;

      const key = `${channelPartnerName}::${advertiserName}`;
      if (advertiserValidationMap.has(key)) continue;

      try {
        const cpData = await queryOrgs({
          first: 1,
          skip: 0,
          where: {
            name: channelPartnerName,
          },
        });

        if (cpData?.orgs && cpData.orgs.length > 0) {
          const channelPartnerId = cpData.orgs[0].id;

          const advData = await queryOrgs({
            first: 1,
            skip: 0,
            where: {
              AND: [
                {
                  OR: [
                    { parentOrg_some: { id: channelPartnerId } },
                    { id: channelPartnerId },
                  ],
                },
                {
                  name: advertiserName,
                },
              ],
            },
          });

          if (!advData?.orgs || advData.orgs.length === 0) {
            const affectedRows = rows
              .filter(r =>
                r.data['Channel Partner Name*']?.trim() === channelPartnerName &&
                r.data['Advertiser Name*']?.trim() === advertiserName
              )
              .map(r => r.lineNumber);

            errors.push(
              `❌ Rows ${affectedRows.join(', ')}: Advertiser "${advertiserName}" not found under Channel Partner "${channelPartnerName}". Please verify the advertiser exists and is associated with this channel partner.`
            );
          } else {
            foundOrgs[`adv_${channelPartnerName}::${advertiserName}`] = advData.orgs[0];
          }
        }

        advertiserValidationMap.set(key, true);
      } catch (error) {
        errors.push(
          `❌ Failed to verify Advertiser "${advertiserName}": ${error.message}`
        );
      }
    }

    for (const assigneeOrgName of assigneeOrgNames) {
      try {
        const data = await queryOrgs({
          first: 1,
          skip: 0,
          where: {
            AND: [
              {
                OR: [
                  { parentOrg_some: { id: currentUser.role.org.id } },
                  { id: currentUser.role.org.id },
                ],
              },
              {
                name: assigneeOrgName,
              },
            ],
            OR: [
              {
                roles_some: {
                  org: {
                    level: 'PLATFORM',
                  },
                  roleItems_some: {
                    feature: 'TASK_MANAGEMENT',
                  },
                },
              },
            ],
          },
        });

        if (!data?.orgs || data.orgs.length === 0) {
          const affectedRows = rows
            .filter(r => r.data['Assignee Org Name']?.trim() === assigneeOrgName)
            .map(r => r.lineNumber);

          const orgExistsCheck = await queryOrgs({
            first: 1,
            skip: 0,
            where: {
              name: assigneeOrgName,
            },
          });

          if (orgExistsCheck?.orgs && orgExistsCheck.orgs.length > 0) {
            errors.push(
              `❌ Rows ${affectedRows.join(', ')}: Assignee Org "${assigneeOrgName}" does not have TASK_MANAGEMENT access or is not within your organization hierarchy. Only organizations with task management permissions can be assigned.`
            );
          } else {
            errors.push(
              `❌ Rows ${affectedRows.join(', ')}: Assignee Org "${assigneeOrgName}" not found in system. Please verify the organization name matches exactly (case-sensitive).`
            );
          }
        } else {
          foundOrgs[`assignee_${assigneeOrgName}`] = data.orgs[0];
        }
      } catch (error) {
        errors.push(
          `❌ Failed to verify Assignee Org "${assigneeOrgName}": ${error.message}`
        );
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      foundOrgs,
    };
  };

  const handleFileUpload = async (file) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      message.error('Please upload a CSV or Excel file');
      return false;
    }

    try {
      message.loading('Parsing and validating file...', 0);
      const rows = await parseCSVFile(file);

      if (rows.length === 0) {
        message.destroy();
        message.error('CSV file is empty or contains no valid data rows');
        return false;
      }

      setCsvFile(file);
      setParsedRows(rows);

      const formatValidation = validateAllRows(rows);

      message.loading('Validating organizations...', 0);
      const orgValidation = await validateOrganizations(rows);

      message.destroy();

      if (orgValidation.foundOrgs) {
        setOrgMappings(orgValidation.foundOrgs);
      }

      const combinedValidation = {
        isValid: formatValidation.isValid && orgValidation.isValid,
        errors: [...formatValidation.errors, ...orgValidation.errors],
        warnings: [...formatValidation.warnings, ...orgValidation.warnings],
      };

      setValidationResult(combinedValidation);

      if (!combinedValidation.isValid) {
        message.error(`Found ${combinedValidation.errors.length} validation error(s)`);
        setCurrentStep(1);
      } else if (combinedValidation.warnings.length > 0) {
        message.warning(`Validation passed with ${combinedValidation.warnings.length} warning(s) - proceeding to preview`);
        setCurrentStep(2);
      } else {
        message.success(`Successfully validated ${rows.length} task(s) - proceeding to preview`);
        setCurrentStep(2);
      }
    } catch (error) {
      message.destroy();
      message.error(error.message || 'Failed to parse CSV file');
    }

    return false;
  };

  const handleImport = async () => {
    if (!validationResult || !validationResult.isValid) {
      message.error('Please fix validation errors before importing');
      return;
    }

    setImporting(true);
    setImportProgress({ current: 0, total: parsedRows.length });
    setCurrentStep(3);

    const results = { success: [], failed: [] };

    for (let i = 0; i < parsedRows.length; i++) {
      const row = parsedRows[i];
      setImportProgress({ current: i + 1, total: parsedRows.length });

      try {
        const orgMappings = await resolveOrganizationsAndUsers(row.data);

        const taskData = buildTaskCreateData(row.data, currentUser, orgMappings);

        await createTask({
          variables: { data: taskData },
        });

        results.success.push({
          lineNumber: row.lineNumber,
          taskName: row.data['Task Name*'],
        });
      } catch (error) {
        results.failed.push({
          lineNumber: row.lineNumber,
          taskName: row.data['Task Name*'],
          error: error.message,
        });
      }
    }

    setImportResults(results);
    setImporting(false);

    if (results.failed.length === 0) {
      message.success(`Successfully imported ${results.success.length} task(s)`, 5);
      if (onSuccess) {
        onSuccess();
      }
      setTimeout(() => {
        handleClose();
      }, 500);
    } else if (results.success.length === 0) {
      message.error(`Failed to import all ${results.failed.length} task(s)`);
    } else {
      message.warning(
        `Imported ${results.success.length} task(s), failed ${results.failed.length} task(s)`
      );
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const resolveOrganizationsAndUsers = async (rowData) => {
    const channelPartnerName = rowData['Channel Partner Name*']?.trim();
    const advertiserName = rowData['Advertiser Name*']?.trim();
    const assigneeOrgName = rowData['Assignee Org Name']?.trim();

    const mappings = {
      channelPartnerOrg: null,
      advertiserOrg: null,
      assigneeOrg: null,
    };

    if (channelPartnerName) {
      try {
        const data = await queryOrgs({
          first: 1000,
          skip: 0,
          where: {
            name: channelPartnerName,
          },
        });

        if (data?.orgs && data.orgs.length > 0) {
          mappings.channelPartnerOrg = data.orgs[0];
        } else {
          throw new Error(`Channel Partner "${channelPartnerName}" not found. Please verify the organization name matches exactly (case-sensitive).`);
        }
      } catch (error) {
        throw new Error(`Failed to fetch Channel Partner "${channelPartnerName}": ${error.message}`);
      }
    }

    if (advertiserName) {
      const channelPartnerId = mappings.channelPartnerOrg?.id || currentUser.role.org.id;

      try {
        const data = await queryOrgs({
          first: 1000,
          skip: 0,
          where: {
            AND: [
              {
                OR: [
                  { parentOrg_some: { id: channelPartnerId } },
                  { id: channelPartnerId },
                ],
              },
              {
                name: advertiserName,
              },
            ],
          },
        });

        if (data?.orgs && data.orgs.length > 0) {
          mappings.advertiserOrg = data.orgs[0];
        } else {
          throw new Error(`Advertiser "${advertiserName}" not found under Channel Partner.`);
        }
      } catch (error) {
        throw new Error(`Failed to fetch Advertiser "${advertiserName}": ${error.message}`);
      }
    }

    if (assigneeOrgName) {
      try {
        const data = await queryOrgs({
          first: 1000,
          skip: 0,
          where: {
            name: assigneeOrgName,
          },
        });

        if (data?.orgs && data.orgs.length > 0) {
          mappings.assigneeOrg = data.orgs[0];
        } else {
          throw new Error(`Assignee Org "${assigneeOrgName}" not found`);
        }
      } catch (error) {
        throw new Error(`Failed to fetch Assignee Org: ${error.message}`);
      }
    }

    return mappings;
  };

  const handleReset = () => {
    setCurrentStep(0);
    setCsvFile(null);
    setParsedRows([]);
    setValidationResult(null);
    setImportResults({ success: [], failed: [] });
    setImportProgress({ current: 0, total: 0 });
    setOrgMappings({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <div style={{ display: 'flex', flexDirection: windowWidth < 768 ? 'column' : 'row', gap: '16px', marginBottom: '24px' }}>
              {/* Step 1: Download */}
              <div style={{
                flex: 1,
                border: '1px solid #d9d9d9',
                borderRadius: '8px',
                padding: windowWidth < 768 ? '16px' : '24px',
                backgroundColor: '#fafafa'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <Title level={5} style={{ marginBottom: '8px' }}>1. Download Template</Title>
                  <Paragraph type="secondary" style={{ fontSize: '13px', marginBottom: '16px' }}>
                    Excel template with proper formatting
                  </Paragraph>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadTemplate}
                    size={windowWidth < 768 ? 'middle' : 'large'}
                    block
                  >
                    Download Template
                  </Button>
                </div>
              </div>

              {/* Step 2: Upload */}
              <div style={{
                flex: 1,
                border: '2px dashed #d9d9d9',
                borderRadius: '8px',
                padding: windowWidth < 768 ? '16px' : '24px',
                backgroundColor: csvFile ? '#f6ffed' : '#ffffff'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <Title level={5} style={{ marginBottom: '8px' }}>2. Upload File</Title>
                  <Paragraph type="secondary" style={{ fontSize: '13px', marginBottom: '16px' }}>
                    Fill template and upload here
                  </Paragraph>
                  <Upload
                    accept=".csv,.xlsx"
                    beforeUpload={handleFileUpload}
                    showUploadList={false}
                    maxCount={1}
                    disabled={csvFile}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      size={windowWidth < 768 ? 'middle' : 'large'}
                      block
                      type={csvFile ? 'default' : 'primary'}
                      disabled={csvFile}
                    >
                      {'Upload File'}
                    </Button>
                  </Upload>
                  {csvFile && (
                    <Text type="success" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                      <CheckCircleOutlined /> {csvFile.name}
                    </Text>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Guide */}
            <div style={{
              border: '1px solid #e6f7ff',
              borderRadius: '8px',
              padding: windowWidth < 768 ? '12px' : '20px',
              backgroundColor: '#f0f8ff',
              marginBottom: '16px'
            }}>
              <Title level={5} style={{ marginTop: '0', marginBottom: '16px', color: '#0050b3', fontSize: windowWidth < 768 ? '14px' : '16px' }}>
                Quick Guide
              </Title>
              <div style={{ display: 'flex', flexDirection: windowWidth < 768 ? 'column' : 'row', gap: windowWidth < 768 ? '16px' : '32px' }}>
                {/* Required Fields */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '12px',
                    gap: '8px'
                  }}>
                    <WarningOutlined style={{ color: '#faad14', fontSize: '16px' }} />
                    <Text strong style={{ fontSize: '14px' }}>Required Fields</Text>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {['Task Name', 'Request Type', 'Due Date', 'Channel Partner Name', 'Advertiser Name'].map((field, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '4px 8px',
                        backgroundColor: '#ffffff',
                        borderRadius: '4px'
                      }}>
                        <div style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: '#faad14'
                        }} />
                        <Text style={{ fontSize: '13px' }}>{field}</Text>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Instructions */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '12px',
                    gap: '8px'
                  }}>
                    <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                    <Text strong style={{ fontSize: '14px' }}>How It Works</Text>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {['Download and open template', 'Fill in your tasks', 'Save and upload file'].map((step, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '4px 8px',
                        backgroundColor: '#ffffff',
                        borderRadius: '4px'
                      }}>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#1890ff',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}>
                          {index + 1}
                        </div>
                        <Text style={{ fontSize: '13px' }}>{step}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Field Reference - Collapsed by default */}
            <Collapse
              bordered={false}
              style={{ marginBottom: '24px' }}
            >
              <Panel
                header={
                  <Text strong>
                    <InfoCircleOutlined style={{ marginRight: '8px' }} />
                    Field Reference Guide
                  </Text>
                }
                key="1"
              >
                <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                  <Title level={5}>Request Type* (Required)</Title>
                  <Paragraph>Must be EXACTLY one of these values:</Paragraph>
                  <div style={{ backgroundColor: '#fafafa', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
                    {taskTypes.map((type, index) => (
                      <Tag key={index} style={{ marginBottom: '4px' }}>{type}</Tag>
                    ))}
                  </div>

                  <Title level={5}>Request Sub Type (Optional)</Title>
                  <Paragraph>Valid values depend on Request Type:</Paragraph>
                  <div style={{ backgroundColor: '#fafafa', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
                    {Object.keys(subTypes).map((type) => (
                      <div key={type} style={{ marginBottom: '12px' }}>
                        <Text strong>{type}:</Text>
                        <div style={{ marginTop: '4px' }}>
                          {subTypes[type].map((subType, index) => (
                            <Tag key={index} color="blue" style={{ marginBottom: '4px', fontSize: '11px' }}>
                              {subType}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <Title level={5}>Urgency (Optional)</Title>
                  <Paragraph>Must be one of these values (or leave blank):</Paragraph>
                  <div style={{ backgroundColor: '#fafafa', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
                    {Object.values(urgencyTypes).map((urgency, index) => (
                      <Tag key={index} color={urgency === 'CRITICAL' ? 'red' : urgency === 'HIGH' ? 'orange' : urgency === 'MEDIUM' ? 'gold' : 'default'}>
                        {urgency}
                      </Tag>
                    ))}
                  </div>

                  <Title level={5}>Dates (Optional)</Title>
                  <ul>
                    <li><strong>Start Date</strong> - Format: YYYY-MM-DD (e.g., 2025-12-31)</li>
                    <li><strong>Due Date</strong> - Format: YYYY-MM-DD, must be on or after Start Date</li>
                  </ul>

                  <Title level={5}>Organizations</Title>
                  <ul>
                    <li><strong>Channel Partner Name*</strong> - Required, must exactly match (case-sensitive)</li>
                    <li><strong>Advertiser Name*</strong> - Required, must be under the Channel Partner</li>
                    <li><strong>Assignee Org Name</strong> - Optional, must have TASK_MANAGEMENT access</li>
                  </ul>

                  <Title level={5}>Additional Fields</Title>
                  <ul>
                    <li><strong>Task Details</strong> - Optional, max 3000 characters, supports markdown</li>
                    <li><strong>Link URL</strong> - Optional, max 499 characters</li>
                  </ul>

                  <Alert
                    message="Pro Tips"
                    description={
                      <ul style={{ marginBottom: '0', paddingLeft: '20px' }}>
                        <li>Copy/paste organization names from the system to avoid typos</li>
                        <li>Start with 2-3 test tasks before doing large imports</li>
                        <li>Organization names are case-sensitive</li>
                        <li>Leave optional fields blank if not needed</li>
                      </ul>
                    }
                    type="info"
                    showIcon
                  />
                </div>
              </Panel>
            </Collapse>
          </div>
        );

      case 1:
        return (
          <div>
            <Title level={4} style={{ marginBottom: '16px' }}>
              {validationResult?.isValid ? '✅ Step 2: Validation Successful!' : '⚠️ Step 2: Validation Results'}
            </Title>

            {validationResult && (
              <>
                {validationResult.isValid ? (
                  <Alert
                    message="✅ Validation Successful!"
                    description={
                      <div>
                        <div>Successfully validated {parsedRows.length} task(s):</div>
                        <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                          <li>✓ Field format and data validation passed</li>
                          <li>✓ All organizations verified in the system</li>
                          <li>✓ Channel Partner and Advertiser relationships confirmed</li>
                        </ul>
                        {validationResult.warnings.length > 0 && (
                          <div style={{ marginTop: '8px' }}>
                            ⚠️ {validationResult.warnings.length} warning(s) detected - you can still proceed
                          </div>
                        )}
                      </div>
                    }
                    type="success"
                    showIcon
                    icon={<CheckCircleOutlined />}
                    style={{ marginBottom: '20px' }}
                  />
                ) : (
                  <>
                    <Alert
                      message="Validation Failed"
                      description={`Found ${validationResult.errors.length} error(s). Please fix these issues and re-upload your file.`}
                      type="error"
                      showIcon
                      icon={<CloseCircleOutlined />}
                      style={{ marginBottom: '20px' }}
                    />
                  </>
                )}

                {validationResult.errors.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <Title level={5}>
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> Errors (must be fixed):
                    </Title>
                    <List
                      size="small"
                      bordered
                      dataSource={validationResult.errors}
                      renderItem={(error) => (
                        <List.Item>
                          <Text type="danger">{error}</Text>
                        </List.Item>
                      )}
                      style={{
                        maxHeight: '200px',
                        overflow: 'auto',
                        backgroundColor: '#fff2f0',
                      }}
                    />
                  </div>
                )}

                {validationResult.warnings.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <Title level={5}>
                      <WarningOutlined style={{ color: '#faad14' }} /> Warnings (review
                      recommended):
                    </Title>
                    <List
                      size="small"
                      bordered
                      dataSource={validationResult.warnings}
                      renderItem={(warning) => (
                        <List.Item>
                          <Text type="warning">{warning}</Text>
                        </List.Item>
                      )}
                      style={{
                        maxHeight: '150px',
                        overflow: 'auto',
                        backgroundColor: '#fffbe6',
                      }}
                    />
                  </div>
                )}

                {validationResult.isValid && (
                  <>
                    <Alert
                      message="Next Step: Preview Tasks"
                      description="Review all tasks that will be imported before proceeding."
                      type="info"
                      showIcon
                      style={{ marginBottom: '20px' }}
                    />
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                      <Button
                        type="primary"
                        onClick={() => setCurrentStep(2)}
                        size="large"
                        icon={<EyeOutlined />}
                        style={{ minWidth: '250px' }}
                      >
                        Preview {parsedRows.length} Task(s)
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        );

      case 2:
        return (
          <div>
            <Title level={4} style={{ marginBottom: '16px' }}>
              👁️ Step 3: Preview Tasks Before Import
            </Title>

            <Alert
              message="Review Your Tasks"
              description="Please review all tasks below. Click on any task to expand/collapse details. If everything looks correct, proceed with import. Otherwise, go back and re-upload your file."
              type="info"
              showIcon
              style={{ marginBottom: '20px' }}
            />

            {validationResult?.warnings && validationResult.warnings.length > 0 && (
              <Alert
                message={`⚠️ ${validationResult.warnings.length} Warning(s) Detected`}
                description={
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      Please review the warnings below. You can still proceed with import, but these items may need attention:
                    </div>
                    <List
                      size="small"
                      bordered
                      dataSource={validationResult.warnings}
                      renderItem={(warning) => (
                        <List.Item style={{ padding: '8px 12px' }}>
                          <Text>{warning}</Text>
                        </List.Item>
                      )}
                      style={{
                        maxHeight: '120px',
                        overflow: 'auto',
                        backgroundColor: '#fffbe6',
                        marginTop: '8px'
                      }}
                    />
                  </div>
                }
                type="warning"
                showIcon
                style={{ marginBottom: '20px' }}
              />
            )}

            <div style={{ marginBottom: '20px' }}>
              <Text strong style={{ fontSize: '16px' }}>
                Total Tasks: {parsedRows.length}
              </Text>
            </div>

            <div style={{ maxHeight: '400px', overflow: 'auto', marginBottom: '20px' }}>
              <Collapse
                defaultActiveKey={parsedRows.map((_, index) => index.toString())}
                style={{ backgroundColor: '#fff' }}
              >
                {parsedRows.map((row, index) => (
                  <Panel
                    key={index.toString()}
                    header={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Tag color="blue">Task #{index + 1}</Tag>
                        <Text strong>{row.data['Task Name*']}</Text>
                      </div>
                    }
                    style={{
                      marginBottom: '8px',
                      backgroundColor: '#fafafa'
                    }}
                  >
                    <div style={{
                      display: 'grid',
                      gap: '16px 32px',
                      padding: '12px 16px',
                      backgroundColor: '#ffffff',
                      borderRadius: '4px'
                    }}>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Request Type</Text>
                        <Text strong style={{ fontSize: '14px' }}>{row.data['Request Type*'] || '-'}</Text>
                      </div>
                      {row.data['Request Sub Type'] && (
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Request Sub Type</Text>
                          <Text strong style={{ fontSize: '14px' }}>{row.data['Request Sub Type']}</Text>
                        </div>
                      )}
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Channel Partner</Text>
                        <Text strong style={{ fontSize: '14px' }}>
                          {getSystemOrgName(row.data['Channel Partner Name*'], 'channelPartner') || '-'}
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Advertiser</Text>
                        <Text strong style={{ fontSize: '14px' }}>
                          {getSystemOrgName(row.data['Advertiser Name*'], 'advertiser', row.data['Channel Partner Name*']) || '-'}
                        </Text>
                      </div>
                      {row.data['Start Date (YYYY-MM-DD)'] && (
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Start Date</Text>
                          <Text strong style={{ fontSize: '14px' }}>{row.data['Start Date (YYYY-MM-DD)']}</Text>
                        </div>
                      )}
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Due Date</Text>
                        <Text strong style={{ fontSize: '14px' }}>{row.data['Due Date* (YYYY-MM-DD)'] || '-'}</Text>
                      </div>
                      {row.data['Urgency'] && (
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>Urgency</Text>
                          <Tag color={
                            row.data['Urgency'] === 'CRITICAL' ? 'red' :
                            row.data['Urgency'] === 'HIGH' ? 'orange' :
                            row.data['Urgency'] === 'MEDIUM' ? 'gold' : 'default'
                          }>
                            {row.data['Urgency']}
                          </Tag>
                        </div>
                      )}
                      {row.data['Assignee Org Name'] && (
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Assignee Org</Text>
                          <Text strong style={{ fontSize: '14px' }}>
                            {getSystemOrgName(row.data['Assignee Org Name'], 'assignee')}
                          </Text>
                        </div>
                      )}
                      {row.data['Task Details'] && (
                        <div style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>Task Details</Text>
                          <div style={{
                            padding: '12px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            border: '1px solid #e8e8e8',
                            maxHeight: '80px',
                            overflow: 'auto'
                          }}>
                            <Text style={{ fontSize: '13px', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                              {row.data['Task Details']}
                            </Text>
                          </div>
                        </div>
                      )}
                      {row.data['Link URL'] && (
                        <div style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>Link URL</Text>
                          <a href={row.data['Link URL']} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px' }}>
                            {row.data['Link URL']}
                          </a>
                        </div>
                      )}
                    </div>
                  </Panel>
                ))}
              </Collapse>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              marginTop: '20px',
              padding: '16px',
              backgroundColor: '#fafafa',
              borderRadius: '8px'
            }}>
              <Button
                size="large"
                onClick={handleReset}
                style={{ minWidth: '180px' }}
              >
                ← Cancel & Re-upload
              </Button>
              <Button
                type="primary"
                onClick={handleImport}
                size="large"
                disabled={importing}
                style={{ minWidth: '220px' }}
              >
                ✓ Confirm & Import {parsedRows.length} Task(s)
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            {importing ? (
              <div style={{ textAlign: 'center' }}>
                <Title level={4} style={{ marginBottom: '16px' }}>
                  ⏳ Step 4: Importing Tasks...
                </Title>
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
                  style={{ marginBottom: '20px' }}
                />
                <Title level={4}>
                  Processing task {importProgress.current} of {importProgress.total}
                </Title>
                <Progress
                  percent={Math.round((importProgress.current / importProgress.total) * 100)}
                  status="active"
                />
                <Paragraph type="secondary" style={{ marginTop: '16px' }}>
                  Do not close this window.
                </Paragraph>
              </div>
            ) : (
              <div>
                <Title level={4} style={{ marginBottom: '16px' }}>
                  {importResults.failed.length === 0 ? '✅ All Tasks Imported Successfully!' : '⚠️ Import Completed with Some Errors'}
                </Title>

                <Alert
                  message={
                    importResults.failed.length === 0
                      ? 'Import Successful!'
                      : 'Import Partially Completed'
                  }
                  description={
                    importResults.failed.length === 0
                      ? `Successfully created ${importResults.success.length} task(s).`
                      : `Imported ${importResults.success.length} task(s). ${importResults.failed.length} failed.`
                  }
                  type={importResults.failed.length === 0 ? 'success' : 'warning'}
                  showIcon
                  style={{ marginBottom: '20px' }}
                />

                {importResults.success.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <Title level={5}>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} /> Successfully Imported:
                    </Title>
                    <List
                      size="small"
                      bordered
                      dataSource={importResults.success}
                      renderItem={(item) => (
                        <List.Item>
                          <Text>
                            Row {item.lineNumber}: {item.taskName}
                          </Text>
                        </List.Item>
                      )}
                      style={{
                        maxHeight: '150px',
                        overflow: 'auto',
                        backgroundColor: '#f6ffed',
                      }}
                    />
                  </div>
                )}

                {importResults.failed.length > 0 && (
                  <>
                    <div style={{ marginBottom: '20px' }}>
                      <Title level={5}>
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> Failed to Import:
                      </Title>
                      <List
                        size="small"
                        bordered
                        dataSource={importResults.failed}
                        renderItem={(item) => (
                          <List.Item>
                            <div>
                              <Text strong>
                                Row {item.lineNumber}: {item.taskName}
                              </Text>
                              <br />
                              <Text type="danger">{item.error}</Text>
                            </div>
                          </List.Item>
                        )}
                        style={{
                          maxHeight: '200px',
                          overflow: 'auto',
                          backgroundColor: '#fff2f0',
                        }}
                      />
                    </div>

                    <Alert
                      message="Common Errors"
                      description={
                        <ul style={{ marginBottom: '0', paddingLeft: '20px' }}>
                          <li>"not found" - Check exact name match (case-sensitive)</li>
                          <li>"does not have access" - User needs role in organization</li>
                          <li>"not found under Channel Partner" - Verify parent-child relationship</li>
                        </ul>
                      }
                      type="info"
                      showIcon
                      style={{ marginBottom: '12px' }}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FileTextOutlined />
          Bulk Task Import
        </div>
      }
      width={windowWidth < 768 ? '95%' : 800}
      open={visible}
      onCancel={importing ? null : handleClose}
      closable={!importing}
      maskClosable={!importing}
      footer={
        currentStep === 3 && !importing ? (
          <div>
            <Button onClick={handleClose}>Close</Button>
            <Button type="primary" onClick={handleReset}>
              Import More Tasks
            </Button>
          </div>
        ) : importing ? null : (
          <div>
            <Button onClick={handleClose}>Cancel</Button>
            {currentStep > 0 && currentStep < 3 && (
              <Button onClick={handleReset}>Start Over</Button>
            )}
          </div>
        )
      }
    >
      <Steps current={currentStep} size="small" direction={windowWidth < 768 ? 'vertical' : 'horizontal'} style={{ marginBottom: '30px' }}>
        <Step title="Upload & Validate" />
        <Step title="Validation Results" />
        <Step title="Preview & Confirm" />
        <Step title="Import" />
      </Steps>

      {renderStepContent()}
    </Modal>
  );
};

export default BulkTaskImportModal;
